import axios from 'axios';
import pLimit from 'p-limit';
import { defineStore, storeToRefs } from 'pinia';
import { Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import {
  DomainBranding,
  DomainReference,
  IdKeyedMapped,
  Loadable,
} from '@rygen/rygen-ui/model';

import api from '@/api';

import { useCurrentUserStore } from './CurrentUser';

// caps concurrent getBranding calls to not exceed nginx http2_max_concurrent_streams
const brandingLimit = pLimit(10);

type IdToDomainMap = IdKeyedMapped<Loadable<DomainReference>>;

type IdToDomainBrandingMap = IdKeyedMapped<Loadable<DomainBranding>>;

export interface PerformLoadOptions {
  bypassCache: boolean;
}

export interface DomainStore {
  domainBrandings: Ref<IdToDomainBrandingMap>;
  domains: Ref<IdToDomainMap>;
  loadDomain: (
    id: number,
    opts?: PerformLoadOptions,
  ) => Promise<Loadable<DomainReference>>;
  loadDomainBranding: (
    id: number,
    opts?: PerformLoadOptions,
  ) => Promise<Loadable<DomainBranding>>;
  updateBranding: (id: number, branding: DomainBranding) => void;
}

const PERFORM_LOAD_DEFAULT_OPTIONS: PerformLoadOptions = {
  bypassCache: false,
};

const _useDomainStore = defineStore('domain', (): DomainStore => {
  const { selectedDomainId } = useCurrentUserStore();
  const { t } = useI18n();
  const toast = useToast();

  const domains: Ref<IdToDomainMap> = ref({});

  const domainBrandings: Ref<IdToDomainBrandingMap> = ref({});

  const loadDomain = async (
    id: number,
    opts: PerformLoadOptions = PERFORM_LOAD_DEFAULT_OPTIONS,
  ): Promise<Loadable<DomainReference>> => {
    const options = { ...PERFORM_LOAD_DEFAULT_OPTIONS, ...opts };
    if (!domains.value[id]) {
      domains.value[id] = { loading: false };
    }

    const domain = domains.value[id];
    const requiresLoad = !domain.data && !domain.loading;

    return options.bypassCache || requiresLoad ? performDomainLoad(id) : domain;
  };

  const performDomainLoad = async (
    id: number,
  ): Promise<Loadable<DomainReference>> => {
    const domain = domains.value[id]!;

    try {
      domain.loading = true;
      domain.data = await api.domain.getById(id);
    } catch (e) {
      if (axios.isCancel(e)) {
        return domain;
      }

      domain.error = e;
      console.error('Error loading domain', e);
      toast.addError({
        detail: t('errors.loadingParam', {
          param: t('common.domains', 2),
        }),
        error: e,
      });
    } finally {
      domain.loading = false;
      domains.value[id] = domain;
    }

    return domain;
  };

  const loadDomainBranding = async (
    id: number,
    opts: PerformLoadOptions = PERFORM_LOAD_DEFAULT_OPTIONS,
  ): Promise<Loadable<DomainBranding>> => {
    const options = { ...PERFORM_LOAD_DEFAULT_OPTIONS, ...opts };
    if (!domainBrandings.value[id]) {
      domainBrandings.value[id] = { loading: false };
    }

    const domainBranding = domainBrandings.value[id];
    const requiresLoad = !domainBranding.data && !domainBranding.loading;

    if ((options.bypassCache && !domainBranding.loading) || requiresLoad) {
      domainBranding.loading = true;
      return brandingLimit(() => performDomainBrandingLoad(id));
    }
    return domainBranding;
  };

  const performDomainBrandingLoad = async (
    id: number,
  ): Promise<Loadable<DomainBranding>> => {
    domainBrandings.value[id] ??= { loading: false };
    const domainBranding = domainBrandings.value[id];

    try {
      domainBranding.loading = true;
      domainBranding.data = await api.domain.getBranding(id);
    } catch (e) {
      if (axios.isCancel(e)) {
        return domainBranding;
      }

      domainBranding.error = e;
      console.error('Error loading domain branding', e);
    } finally {
      domainBranding.loading = false;
      domainBrandings.value[id] = domainBranding;
    }

    return domainBranding;
  };

  watch(selectedDomainId, () => {
    domains.value = {};
    domainBrandings.value = {};
  });

  const updateBranding = (id: number, branding: DomainBranding) => {
    domainBrandings.value[id] = { loading: false, data: branding };
  };

  return {
    domainBrandings,
    domains,
    loadDomain,
    loadDomainBranding,
    updateBranding,
  };
});

export const useDomainStore = (): DomainStore => {
  const store = _useDomainStore();
  const { domainBrandings, domains } = storeToRefs(store);
  const { loadDomain, loadDomainBranding, updateBranding } = store;

  return {
    domainBrandings,
    domains,
    loadDomain,
    loadDomainBranding,
    updateBranding,
  };
};
